import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  FiArrowRight,
  FiCheckCircle,
  FiCheckSquare,
  FiClock,
  FiFileText,
  FiInbox,
  FiLock,
} from "react-icons/fi";
import MicrosoftSignInButton from "../components/MicrosoftSignInButton";

function Home({ isSignedIn, user, canManageTasks }) {
  const { openFormsMenu } = useOutletContext();

  if (!isSignedIn) {
    return (
      <div className="flex min-h-[calc(100vh-64px)] items-center bg-[#f5f6f8] px-6 py-12 lg:px-12">
        <section className="mx-auto grid min-h-[470px] w-full max-w-6xl overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm lg:grid-cols-[1.05fr_0.95fr]">
          <div className="flex flex-col justify-center px-8 py-12 sm:px-12 lg:px-14">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#0f6cbd]">
              <FiLock className="h-4 w-4" aria-hidden="true" />
              <span>Espace sécurisé du personnel</span>
            </div>

            <h1 className="mt-5 max-w-xl text-[28px] font-bold leading-9 text-slate-900">
              Accédez à vos formulaires et à vos demandes
            </h1>
            <p className="mt-4 max-w-xl text-[15px] leading-6 text-slate-600">
              Utilisez votre compte professionnel pour soumettre un formulaire,
              consulter l’avancement de vos demandes et traiter les tâches qui
              vous sont attribuées.
            </p>

            <div className="mt-8">
              <MicrosoftSignInButton />
            </div>

            <p className="mt-5 text-xs leading-5 text-slate-500">
              Accès réservé aux membres du personnel du Conseil scolaire
              Viamonde.
            </p>
          </div>

          <div className="flex items-center border-t border-slate-200 bg-slate-50 px-8 py-10 sm:px-12 lg:border-l lg:border-t-0">
            <div className="w-full">
              <div className="mb-5">
                <p className="text-base font-semibold text-slate-900">
                  Suivi de vos demandes
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  Consultez chaque étape depuis un seul espace.
                </p>
              </div>

              <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
                <div className="flex items-center gap-4 border-b border-slate-100 px-5 py-4">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-blue-50 text-[#0f6cbd]">
                    <FiFileText className="h-[18px] w-[18px]" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-800">
                      Demande soumise
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      Votre formulaire a été transmis.
                    </p>
                  </div>
                  <span className="h-2.5 w-2.5 rounded-full bg-blue-500" aria-hidden="true" />
                </div>

                <div className="flex items-center gap-4 border-b border-slate-100 px-5 py-4">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-amber-50 text-amber-600">
                    <FiClock className="h-[18px] w-[18px]" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-800">
                      Suivi du traitement
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      Consultez le statut et les mises à jour.
                    </p>
                  </div>
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-500" aria-hidden="true" />
                </div>

                <div className="flex items-center gap-4 px-5 py-4">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-green-50 text-green-600">
                    <FiCheckCircle className="h-[18px] w-[18px]" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-800">
                      Demande approuvée
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      Retrouvez la décision dans votre historique.
                    </p>
                  </div>
                  <span className="h-2.5 w-2.5 rounded-full bg-green-500" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const firstName = user?.givenName || user?.displayName || "";

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#f5f6f8] px-6 py-12 lg:px-12">
      <div className="mx-auto w-full max-w-6xl">
        <div>
          <p className="text-sm font-medium text-[#0f6cbd]">Espace personnel</p>
          <h1 className="mt-2 text-2xl font-bold text-slate-900">
            Bonjour{firstName ? ` ${firstName}` : ""}
          </h1>
          <p className="mt-2 text-[15px] text-slate-600">
            Que souhaitez-vous faire aujourd’hui?
          </p>
        </div>

        <div
          className={`mt-8 grid gap-5 ${
            canManageTasks ? "md:grid-cols-3" : "md:grid-cols-2"
          }`}
        >
          <button
            type="button"
            onClick={openFormsMenu}
            className="group flex min-h-44 w-full flex-col rounded-lg border border-slate-200 bg-white p-6 text-left shadow-sm transition-all hover:border-blue-300 hover:shadow-md"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-50 text-[#0f6cbd]">
              <FiFileText className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="mt-5 text-base font-semibold text-slate-900">
              Créer une demande
            </span>
            <span className="mt-1.5 text-sm leading-5 text-slate-500">
              Sélectionnez le formulaire qui correspond à votre besoin.
            </span>
            <FiArrowRight
              className="mt-auto h-4 w-4 self-end text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-[#0f6cbd]"
              aria-hidden="true"
            />
          </button>

          <Link
            to="/demande"
            className="group flex min-h-44 flex-col rounded-lg border border-slate-200 bg-white p-6 text-left shadow-sm transition-all hover:border-blue-300 hover:shadow-md"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-50 text-[#0f6cbd]">
              <FiInbox className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="mt-5 text-base font-semibold text-slate-900">
              Suivre mes demandes
            </span>
            <span className="mt-1.5 text-sm leading-5 text-slate-500">
              Consultez l’état et l’historique de vos formulaires.
            </span>
            <FiArrowRight
              className="mt-auto h-4 w-4 self-end text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-[#0f6cbd]"
              aria-hidden="true"
            />
          </Link>

          {canManageTasks && (
            <Link
              to="/taches"
              className="group flex min-h-44 flex-col rounded-lg border border-slate-200 bg-white p-6 text-left shadow-sm transition-all hover:border-blue-300 hover:shadow-md"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-50 text-[#0f6cbd]">
                <FiCheckSquare className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="mt-5 text-base font-semibold text-slate-900">
                Traiter mes tâches
              </span>
              <span className="mt-1.5 text-sm leading-5 text-slate-500">
                Accédez aux demandes qui nécessitent votre intervention.
              </span>
              <FiArrowRight
                className="mt-auto h-4 w-4 self-end text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-[#0f6cbd]"
                aria-hidden="true"
              />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default Home;
